import { Badge } from '@/components/common/badge'
import { Button } from '@/components/common/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent, TabsTrigger } from '@/components/common/tabs'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { Vault } from '@/types/vault'
import { formatTokenValue } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { z } from 'zod'

const YO_APP_URL = 'https://app.yo.xyz/'
export const YO_WAIT_FOR_REDEEM_TAB_NAME = 'yoWaitForRedeem'

const claimDataSchema = z.object({
  assets: z.string(),
  shares: z.string(),
})

export type YoSpecificData = {
  pendingRedeemRequest: z.infer<typeof claimDataSchema>
  liquidAssets: string
}

export function YoWaitForRedeemTab({
  vault,
  transactionsContext,
}: {
  vault: Vault
  transactionsContext: TransactionsContext
}) {
  const claimData = useMemo(() => {
    return claimDataSchema.parse((transactionsContext?.vaultSpecificData as YoSpecificData)['pendingRedeemRequest'])
  }, [transactionsContext])

  return (
    <TabsContent value={YO_WAIT_FOR_REDEEM_TAB_NAME} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
      {claimData && (
        <Table>
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                {formatTokenValue(
                  { value: claimData.assets, decimals: vault.asset.decimals },
                  { minimumFractionDigits: 2 }
                )}{' '}
                {vault.asset.symbol}
              </TableCell>
              <TableCell>
                <Badge color={'orange'}>Pending</Badge>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      <div className="flex flex-col gap-2 text-center">
        <p className="font-bold text-heading-foreground text-md">Withdrawal in progress...</p>
        <div className="text-gray-500 text-sm">
          Withdrawals above the liquidity threshold can take up to 24 hours to complete. You can cancel the withdrawal
          in the{' '}
          <Button variant="default" size="default" className="rounded-full text-xs underline" asChild>
            <a href={YO_APP_URL} target="_blank" rel="noreferrer">
              Yo app
            </a>
          </Button>
          .
        </div>
      </div>
    </TabsContent>
  )
}

export const YoWaitForRedeemTabTrigger = ({ className }: { className?: string }) => {
  return (
    <TabsTrigger value={YO_WAIT_FOR_REDEEM_TAB_NAME} className={className}>
      Pending
    </TabsTrigger>
  )
}
